import {
  APIEntityType,
  CommonDefaultFilterKeys,
  FormattingRuleOperator,
  isColorRangeFormatting,
} from '@ardoq/api-types';
import {
  ComponentOrReferenceFormattingRule,
  FormattingRule,
} from '@ardoq/data-model';
import { omit } from 'lodash';

// TODO(typeName-migration): Remove LegacyComponentOrReferenceFormattingRule, legacyTypeNamesToTypeNames,
// migrateTypeNameField, and normalizeRuleTypeNames after BE migrates all stored rules from typeName→typeNames.
// Also simplify normalizeFormattingRules to just call normalizeRule.

// Represents data persisted before the typeName→typeNames rename.
// Backend data may still arrive with the old shape.
export type LegacyComponentOrReferenceFormattingRule = Omit<
  ComponentOrReferenceFormattingRule,
  'typeNames'
> & {
  typeName?: string | null;
  typeNames?: string[] | null;
};

export const legacyTypeNamesToTypeNames = (
  rule: LegacyComponentOrReferenceFormattingRule
): string[] | null => {
  if (rule.typeNames !== undefined) return rule.typeNames;
  return rule.typeName ? [rule.typeName] : null;
};

const migrateTypeNameField = (
  rule: LegacyComponentOrReferenceFormattingRule
): ComponentOrReferenceFormattingRule => {
  if (rule.typeNames !== undefined) {
    return rule as ComponentOrReferenceFormattingRule;
  }
  return {
    ...omit(rule, 'typeName'),
    typeNames: legacyTypeNamesToTypeNames(rule),
  } satisfies ComponentOrReferenceFormattingRule;
};

export function normalizeRuleTypeNames(
  conditionalFormattingRules: FormattingRule[]
): FormattingRule[] {
  return conditionalFormattingRules.map(rule => {
    if (!isComponentOrReferenceRule(rule)) return rule;
    return migrateTypeNameField(rule);
  });
}

const isComponentOrReferenceRule = (
  rule: FormattingRule
): rule is ComponentOrReferenceFormattingRule =>
  rule.type === APIEntityType.COMPONENT ||
  rule.type === APIEntityType.REFERENCE;

export const isTypeRule = (
  rule: FormattingRule
): rule is ComponentOrReferenceFormattingRule =>
  isComponentOrReferenceRule(rule) &&
  rule.field === CommonDefaultFilterKeys.TYPE;

export type GraphItemNamesSets = {
  componentTypeNamesSet: Set<string>;
  referenceTypeNamesSet: Set<string>;
};

export const isValidTypeRule = (
  rule: FormattingRule,
  { componentTypeNamesSet, referenceTypeNamesSet }: GraphItemNamesSets
): boolean => {
  if (typeof rule.value !== 'string') return false;

  if (rule.type === APIEntityType.COMPONENT) {
    return componentTypeNamesSet.has(rule.value);
  }
  if (rule.type === APIEntityType.REFERENCE) {
    return referenceTypeNamesSet.has(rule.value);
  }
  return false;
};

const normalizeComparator = (rule: FormattingRule): FormattingRule =>
  rule.type === APIEntityType.COMPONENT || rule.type === APIEntityType.REFERENCE
    ? {
        ...rule,
        ...(rule.comparator && isColorRangeFormatting(rule.value)
          ? {
              comparator: FormattingRuleOperator.COLOR_RANGE,
            }
          : {}),
      }
    : rule;

/**
 * Normalizes an array of formatting rules by updating their comparator.
 * @deprecated Remove when BE has migrated all rules to v1
 * @param conditionalFormattingRules - The array of formatting rules to normalize.
 * @returns The normalized array of formatting rules.
 */
export function normalizeRule(
  conditionalFormattingRules: FormattingRule[]
): FormattingRule[] {
  return conditionalFormattingRules.map(_rule => {
    return normalizeComparator(_rule);
  });
}

/**
 * Applies all load-time normalization steps in the correct order.
 * We should always use this instead of calling normalizeRule / normalizeRuleTypeNames
 * individually to avoid accidentally skipping a step.
 */
export const normalizeFormattingRules = (
  rules: FormattingRule[]
  // TODO(typeName-migration): Remove normalizeRuleTypeNames call once typeName is gone from all BE data.
): FormattingRule[] => normalizeRuleTypeNames(normalizeRule(rules));
